import React, { useEffect, useState } from "react";

/**
 * Avatar for customer-facing link pages. Uses the page image when available,
 * otherwise falls back to a single clean initial on a deterministic gradient
 * (colours derived from the Deexoptions brand gradient) so "Offering", "Tithe",
 * etc. each get their own distinct, sleek badge instead of flat grey letters.
 */
const GRADIENTS = [
  "from-[#0B75C2] to-[#0EB4E7]", // brand blue → cyan
  "from-[#F85D31] to-[#FFAF26]", // brand orange → amber
  "from-[#6366F1] to-[#8B5CF6]", // indigo → violet
  "from-[#059669] to-[#10B981]", // emerald
  "from-[#DB2777] to-[#F472B6]", // pink
  "from-[#0891B2] to-[#22D3EE]", // teal → sky
];

const hashString = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
};

const PageAvatar = ({
  name,
  image,
  className = "",
  textClassName = "",
}: {
  name?: string;
  image?: string;
  /** Sizing/shadow classes for the circle (e.g. "h-11 w-11"). */
  className?: string;
  /** Font-size class for the initial (e.g. "text-[15px]"). */
  textClassName?: string;
}) => {
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setImgError(false);
  }, [image]);

  const gradient = GRADIENTS[hashString(name || "?") % GRADIENTS.length];
  const initial = (name?.trim()?.charAt(0) || "?").toUpperCase();

  if (image && !imgError) {
    return (
      <div
        className={`flex flex-none items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-white ${className}`}
      >
        <img
          src={image}
          alt={name ? `${name} logo` : "logo"}
          className="h-full w-full object-cover"
          onError={() => setImgError(true)}
        />
      </div>
    );
  }

  return (
    <div
      className={`flex flex-none items-center justify-center rounded-full bg-gradient-to-br font-sora font-bold text-white ${gradient} ${textClassName} ${className}`}
    >
      {initial}
    </div>
  );
};

export default PageAvatar;
